import { euro } from '../core/format/currency'
import type { Policy } from '../core/types/site'

/**
 * SINGLE SOURCE OF TRUTH voor alle zakelijke afspraken.
 * Verander hier een getal (of straks in de backend) en het klopt overal:
 * winkelmand, checkout, FAQ, SEO, balken en juridische pagina's.
 */
export const policy: Policy = {
  freeShippingFrom: 100,
  shippingCost: 5.95,
  returnDays: 30,
  orderCutoff: '15:00',
  yearsExperience: 20,
  responseHours: 24,
  giftConsultMinutes: 20,
  cancelHours: 24,
  refillWarnDays: 14,
  courseSpacingWeeks: 3,
  courseMaxSessions: 20,
  kuurValidMonths: 24,
  kuurTransferable: false,
  noShowUsesSession: false,
  revisitAfterWeeks: 8,
  diaryEveryDays: 14,
  vatRate: 21,
  followUpWeeks: 3,
  giftCardValidMonths: 24,
  // D9 is nog open. "Ook voor producten" past bij de huidige tekst op de bon en in de webshop.
  giftCardValidFor: 'all',
  magicLinkMinutes: 15,
  subscription: {
    giftEvery: 3,
    videoChecksPerYear: 2,
    leadDays: 3,
    changeUntilDays: 2,
    defaultIntervalWeeks: 8,
    intervals: [4, 6, 8, 10, 12],
    pauseOptions: [
      { weeks: 4, label: '1 maand' },
      { weeks: 8, label: '2 maanden' },
      { weeks: 13, label: '3 maanden' },
    ],
  },
  consult: {
    slug: 'shelfie-time',
    name: 'Shelfie Time',
    minutes: 30,
    price: 45,
    creditFrom: 150,
    creditDays: 30,
  },
}

/** De verzendregel, puur: overal dezelfde zin (balk, mand, staffel). */
export const freeShippingLine = (from: number) => `Gratis verzending vanaf ${euro(from)}`

const ordinals = ['', 'eerste', 'tweede', 'derde', 'vierde', 'vijfde', 'zesde', 'zevende', 'achtste', 'negende', 'tiende']
/** 3 → "derde", 12 → "12e" */
export const ordinal = (n: number) => ordinals[n] ?? `${n}e`

/**
 * Kant-en-klare zinnen, afgeleid van `policy` — gebruik deze in plaats van losse tekst.
 * Het zijn getters: ze rekenen altijd met de actuele instellingen.
 */
export const policyCopy = {
  get freeShipping() {
    return freeShippingLine(policy.freeShippingFrom)
  },
  get shippingBelow() {
    return `Onder ${euro(policy.freeShippingFrom)} rekenen we ${euro(policy.shippingCost)} verzendkosten`
  },
  get delivery() {
    return `Voor ${policy.orderCutoff} besteld, morgen in huis`
  },
  get returns() {
    return `${policy.returnDays} dagen ruilen op huidadvies`
  },
  get returnsShort() {
    return `${policy.returnDays} dagen ruilen`
  },
  get response() {
    return `Binnen ${policy.responseHours} uur persoonlijk antwoord`
  },
  get experience() {
    return `${policy.yearsExperience}+ jaar`
  },
  get consultPrice() {
    return euro(policy.consult.price)
  },
  get consultCredit() {
    return `Volledig verrekend bij een bestelling vanaf ${euro(policy.consult.creditFrom)}`
  },
  get cancel() {
    return `Verzetten of annuleren kan tot ${policy.cancelHours} uur van tevoren`
  },
  /** Leeg als de no-show-regel uit staat. */
  get noShow() {
    return policy.noShowUsesSession ? 'Kom je zonder afmelden niet, dan telt de sessie uit je kuur als gebruikt.' : ''
  },
  get courseSpacing() {
    return policy.courseSpacingWeeks === 1 ? 'elke week' : `om de ${policy.courseSpacingWeeks} weken`
  },
  get subscriptionGift() {
    const n = policy.subscription.giftEvery
    return n === 1 ? 'Bij elke levering een cadeau' : `Elke ${ordinal(n)} levering een cadeau`
  },
  /** "Twee jaar geldig" / "18 maanden geldig" */
  get giftCardValidity() {
    const m = policy.giftCardValidMonths
    if (m % 12 !== 0) return `${m} maanden geldig`
    const y = m / 12
    return `${y === 1 ? 'Eén' : y === 2 ? 'Twee' : y === 3 ? 'Drie' : y} jaar geldig`
  },
  /** "Over 3 weken" — wanneer Sarah na een bestelling appt hoe het gaat. */
  get followUp() {
    const n = policy.followUpWeeks
    return n === 1 ? 'Over een week' : `Over ${n} weken`
  },
  /** "altijd gratis verzending, elke derde levering een cadeau" — kleine letter, voor midden in een zin. */
  get subscriptionPerks() {
    const gift = policyCopy.subscriptionGift
    return `altijd gratis verzending, ${gift.charAt(0).toLowerCase()}${gift.slice(1)}`
  },
  get changeUntil() {
    const n = policy.subscription.changeUntilDays
    return n <= 0 ? 'tot de dag van verzending' : `tot ${n === 1 ? 'één dag' : `${n} dagen`} voor de volgende levering`
  },
  get firstDelivery() {
    const n = policy.subscription.leadDays
    return n <= 1 ? 'De eerste levering komt morgen' : `De eerste levering komt over ${n} dagen`
  },
  get videoChecks() {
    const n = policy.subscription.videoChecksPerYear
    return `${n === 1 ? 'Eén huidcheck' : `${n} huidchecks`} per jaar via video`
  },
  payments: 'Wero, Bancontact, Klarna & creditcard',
}

/**
 * Toestemming om huidfoto's te bewaren (AVG art. 9: mogelijk gezondheidsgegevens).
 * Los van "anoniem laten zien" (`Customer.diaryConsent`). Verandert de tekst wezenlijk, verhoog
 * dan de versie: wie een oudere versie gaf, wordt bij de volgende foto opnieuw gevraagd.
 * 🟠 Tekst laten nakijken door een jurist (docs/LARAVEL.md §12.1).
 */
export const photoConsent = {
  version: '2026-10',
  label: 'Ik geef toestemming om mijn huidfoto’s te bewaren in mijn fotodagboek.',
  get hint() {
    return 'Alleen jij en je specialist zien ze. Je kunt dit altijd intrekken; dan worden al je foto’s verwijderd. Zonder intrekken bewaren we ze tot twee jaar na je laatste contact.'
  },
}
